import { GeneriekBerichtOnderwerp, onderwerpen, usePostGeneriekBericht } from "@/entities/bericht";
import { Button, Form, TextAreaFormField, ToggleGroupOption } from "@/shared/components";
import { SingleToggleGroupFormField } from "@/shared/components/ui/single-toggle-group-form-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import z from "zod";
import { onderwerpLabelMap } from "../constants";

const maxMessageLength = 450;

type BerichtComposerProps = {
    dossierId: string;
    onderwerpInput: GeneriekBerichtOnderwerp | GeneriekBerichtOnderwerp[];
};

const nieuwGeneriekBerichtSchema = z.object({
    onderwerp: z.enum(onderwerpen),
    bericht: z
        .string()
        .trim()
        .nonempty({
            message: "Voer een bericht in.",
        })
        .max(maxMessageLength, { message: "Maximaal 450 tekens toegestaan." }),
});

type NieuwGeneriekBerichtData = z.infer<typeof nieuwGeneriekBerichtSchema>;

export function BerichtComposer({ dossierId, onderwerpInput }: BerichtComposerProps) {
    const meerdereOnderwerpen = Array.isArray(onderwerpInput);

    const form = useForm<NieuwGeneriekBerichtData>({
        resolver: zodResolver(nieuwGeneriekBerichtSchema),
        defaultValues: {
            onderwerp: meerdereOnderwerpen ? onderwerpInput[0] : onderwerpInput,
            bericht: "",
        },
    });

    const onderwerp = useWatch({ control: form.control, name: "onderwerp" });

    const postGeneriekBericht = usePostGeneriekBericht();

    const handleSubmit: SubmitHandler<NieuwGeneriekBerichtData> = ({ bericht, onderwerp }) => {
        postGeneriekBericht.mutate({ dossierId, onderwerp, bericht });
        form.reset({
            onderwerp,
            bericht: "",
        });
    };

    const onderwerpOptions: ToggleGroupOption<GeneriekBerichtOnderwerp>[] | null = meerdereOnderwerpen
        ? onderwerpInput.map((onderwerp) => ({ value: onderwerp, label: onderwerp }))
        : null;

    return (
        <Form {...form}>
            <form onSubmit={(e) => void form.handleSubmit(handleSubmit)(e)}>
                <div className="grid gap-2 border-b pb-4">
                    <TextAreaFormField
                        label="Bericht"
                        form={form}
                        name="bericht"
                        className="field-sizing-content max-h-[60vh]"
                        rows={3} //fallback for browsers where field-sizing-content is not supported yet
                        placeholder={
                            onderwerp === "Algemeen" ? "Algemeen wisselbericht" : `Wisselbericht over ${onderwerpLabelMap[onderwerp]}`
                        }
                    />
                    <div className="flex items-center gap-3 justify-self-end">
                        {onderwerpOptions && <SingleToggleGroupFormField options={onderwerpOptions} form={form} name="onderwerp" />}
                        <Button variant="secondary" icon="send" type="submit" disabled={postGeneriekBericht.isPending}>
                            Versturen
                        </Button>
                    </div>
                </div>
            </form>
        </Form>
    );
}
